import { useCallback, useEffect, useState } from 'react';
import { track } from '@lf/analytics-events';
import {
  getSavedVideos,
  saveVideo,
  unsaveVideo,
} from '../../api/lfApiSavedVideos';
import { parseSearchParam } from './parseSearchParam';

const VIDEO_SOURCES = new Set([
  'study_plan', 'rec_path', 'search', 'saved',
  'all_videos', 'home', 'direct',
]);

function _resolveSource() {
  const { source } = parseSearchParam();
  return VIDEO_SOURCES.has(source) ? source : 'direct';
}

export function useSavedVideo({ videoId, channelId, videoMeta, setError }) {
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!videoId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await getSavedVideos(setError);
        const ids = new Set(
          (response?.data?.items || []).map((item) => item.video_id));
        if (!cancelled) setIsSaved(ids.has(videoId));
      } catch (error) {
        console.error('Error loading saved videos:', error);
      }
    })();
    return () => { cancelled = true; };
  }, [videoId, setError]);

  const handleSaveToggle = useCallback(async () => {
    if (!videoId || isSaving) return;
    setIsSaving(true);
    try {
      if (isSaved) {
        const response = await unsaveVideo(videoId, setError);
        if (response) {
          setIsSaved(false);
          track('video_unsaved', { video_id: videoId, source: _resolveSource() });
        }
      } else {
        const payload = {
          id: { videoId },
          snippet: {
            channelId,
            title: videoMeta?.title || '',
            channelTitle: videoMeta?.channelTitle || '',
            thumbnails: {
              high: { url: videoMeta?.thumbnailUrl || '' },
            },
          },
        };
        const response = await saveVideo(payload, setError);
        if (response) {
          setIsSaved(true);
          track('video_saved', { video_id: videoId, source: _resolveSource() });
        }
      }
    } finally {
      setIsSaving(false);
    }
  }, [channelId, isSaved, isSaving, setError, videoId, videoMeta]);

  return { isSaved, isSaving, handleSaveToggle };
}
